import { useState, useEffect, useCallback } from 'react'
import { useFiltersStore } from '@/store/filters'
import { biApi } from '@/lib/api'
import FilterBar from '@/components/FilterBar'
import {
  MousePointerClick,
  CheckCircle2,
  Percent,
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import clsx from 'clsx'

// Chart colors - Orange-centered palette with gradients
const COLORS = [
  '#f97316', // Orange 500 (primary)
  '#fb923c', // Orange 400
  '#ea580c', // Orange 600
  '#fdba74', // Orange 300
  '#c2410c', // Orange 700
  '#ffedd5', // Orange 100
  '#22c55e', // Green (accent)
  '#3b82f6', // Blue (accent)
]

interface DashboardData {
  meta: {
    timezone: string
    date: string
    dateLocal: string
    lastUpdated: string
    refreshInterval: number
    filtersApplied: Record<string, any>
    isAdmin: boolean
  }
  summary: {
    clicks: { total: number; trend: number; trendDirection: string }
    conversions: { total: number; trend: number; trendDirection: string }
    conversionRate: { value: number; trend: number; trendDirection: string }
    uniqueCountries: number
  }
  hourlyTrend: Array<{
    hour: number
    hourLabel: string
    clicks: number
    conversions: number
    rate: number
  }>
  byCountry: Array<{
    country: string
    countryName: string
    flag: string
    clicks: number
    conversions: number
    rate: number
    percentage: number
  }>
  bySource: Array<{
    source: string
    clicks: number
    conversions: number
    rate: number
    percentage: number
    heClicks?: number
    wifiClicks?: number
    heRate?: number
  }>
  bySourceProducts: Array<{
    source: string
    productId: number
    productName: string
    clicks: number
    conversions: number
    rate: number
    heClicks?: number
    wifiClicks?: number
    heRate?: number
  }>
  byOperator: Array<{
    operator: string
    country: string
    clicks: number
    conversions: number
    rate: number
  }>
}

// KPI Card component
function KPICard({
  title,
  value,
  icon: Icon,
  trend,
  trendDirection,
  format = 'number',
  color = 'primary',
}: {
  title: string
  value: number
  icon: React.FC<{ className?: string }>
  trend?: number
  trendDirection?: string
  format?: 'number' | 'percent'
  color?: 'primary' | 'success' | 'warning'
}) {
  const formatValue = (val: number) => {
    if (format === 'percent') {
      return `${val.toFixed(2)}%`
    }
    if (val >= 1000000) {
      return `${(val / 1000000).toFixed(2)}M`
    }
    if (val >= 1000) {
      return `${(val / 1000).toFixed(1)}K`
    }
    return val.toLocaleString()
  }
  
  const colorClasses = {
    primary: 'bg-primary-50 dark:bg-primary-900/20 text-primary-600',
    success: 'bg-success-50 dark:bg-success-500/20 text-success-600',
    warning: 'bg-warning-50 dark:bg-warning-500/20 text-warning-600',
  }
  
  return (
    <div className="kpi-card">
      <div className="flex items-start justify-between">
        <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={clsx(
            'flex items-center gap-1 text-sm font-medium',
            trendDirection === 'up' && 'text-success-500',
            trendDirection === 'down' && 'text-error-500',
            trendDirection === 'stable' && 'text-gray-400'
          )}>
            {trendDirection === 'up' && <TrendingUp className="w-4 h-4" />}
            {trendDirection === 'down' && <TrendingDown className="w-4 h-4" />}
            {trendDirection === 'stable' && <Minus className="w-4 h-4" />}
            <span>{trend > 0 ? '+' : ''}{trend}%</span>
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="kpi-value tabular-nums">{formatValue(value)}</p>
        <p className="kpi-label mt-1">{title}</p>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { filters } = useFiltersStore()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [hourFrom, setHourFrom] = useState(0)
  const [hourTo, setHourTo] = useState(23)
  const [expandedSource, setExpandedSource] = useState<string | null>(null)
  
  const fetchData = useCallback(async () => {
    try {
      setError(null)
      const response = await biApi.getRealtimeMetrics({
        timezone: filters.timezone,
        countries: filters.countries.length > 0 ? filters.countries : undefined,
        operators: filters.operators.length > 0 ? filters.operators : undefined,
        productIds: filters.productIds.length > 0 ? filters.productIds : undefined,
        sources: filters.sources.length > 0 ? filters.sources : undefined,
        customerIds: filters.customerIds.length > 0 ? filters.customerIds : undefined,
        flow: filters.flow || undefined,
      })
      
      if (response.data.success) {
        setData(response.data.data)
        setLastRefresh(new Date())
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar los datos')
    } finally {
      setLoading(false)
    }
  }, [filters])
  
  // Initial load
  useEffect(() => {
    fetchData()
  }, [fetchData])
  
  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [fetchData])
  
  const handleRefresh = () => {
    setLoading(true)
    fetchData()
  }
  
  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-10 h-10 text-primary-500 animate-spin mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Cargando dashboard...</p>
      </div>
    )
  }
  
  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <AlertCircle className="w-10 h-10 text-error-500 mb-4" />
        <p className="text-error-500 font-medium mb-2">{error}</p>
        <button onClick={handleRefresh} className="btn-primary">
          Reintentar
        </button>
      </div>
    )
  }
  
  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {data?.meta.dateLocal}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Live indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-success-500"></span>
            </span>
            <span>En vivo</span>
            {lastRefresh && (
              <span className="text-xs text-gray-400">
                · {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-primary btn-sm"
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Actualizar
          </button>
        </div>
      </div>
      
      {/* Filter bar */}
      <FilterBar onRefresh={handleRefresh} />
      
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Clicks Totales"
          value={data?.summary.clicks.total || 0}
          icon={MousePointerClick}
          trend={data?.summary.clicks.trend}
          trendDirection={data?.summary.clicks.trendDirection}
          color="primary"
        />
        <KPICard
          title="Conversiones"
          value={data?.summary.conversions.total || 0}
          icon={CheckCircle2}
          trend={data?.summary.conversions.trend}
          trendDirection={data?.summary.conversions.trendDirection}
          color="success"
        />
        <KPICard
          title="Tasa de Conversión"
          value={data?.summary.conversionRate.value || 0}
          icon={Percent}
          format="percent"
          color="warning"
        />
        <KPICard
          title="Países Activos"
          value={data?.summary.uniqueCountries || 0}
          icon={Globe}
          color="primary"
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Hourly Trend Chart */}
        <div className="card p-4 hover:shadow-lg transition-shadow duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Tendencia por Hora
            </h3>
            <div className="flex items-center gap-2 text-sm">
              <select
                value={hourFrom}
                onChange={(e) => setHourFrom(Number(e.target.value))}
                className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
              <span className="text-gray-400">→</span>
              <select
                value={hourTo}
                onChange={(e) => setHourTo(Number(e.target.value))}
                className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
          </div>
          <div className="h-64 chart-glow">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={(data?.hourlyTrend || []).filter(h => h.hour >= hourFrom && h.hour <= hourTo)}>
                <defs>
                  <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="conversionsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="hourLabel"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  stroke="#f97316"
                  label={{ value: 'Clicks', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#f97316' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  stroke="#22c55e"
                  label={{ value: 'Conversiones', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#22c55e' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #fed7aa',
                    borderRadius: '8px',
                    boxShadow: '0 4px 15px rgba(249, 115, 22, 0.2)',
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="right"
                  dataKey="conversions"
                  fill="url(#conversionsGradient)"
                  radius={[4, 4, 0, 0]}
                  name="Conversiones"
                  barSize={12}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="clicks"
                  stroke="#f97316"
                  strokeWidth={3}
                  dot={false}
                  name="Clicks"
                  filter="url(#glow)"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Country Distribution */}
        <div className="card p-4 hover:shadow-lg transition-shadow duration-300">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Distribución por País
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(data?.byCountry || []).slice(0, 6)}
                layout="vertical"
              >
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#ea580c" stopOpacity={1}/>
                    <stop offset="50%" stopColor="#f97316" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#fb923c" stopOpacity={1}/>
                  </linearGradient>
                  <filter id="barGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} tickLine={false} />
                <YAxis
                  dataKey="countryName"
                  type="category"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #fed7aa',
                    borderRadius: '8px',
                    boxShadow: '0 4px 15px rgba(249, 115, 22, 0.2)',
                  }}
                />
                <Bar 
                  dataKey="clicks" 
                  fill="url(#barGradient)" 
                  radius={[0, 4, 4, 0]} 
                  name="Clicks"
                  filter="url(#barGlow)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Source Table */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Por Fuente de Tráfico
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    Fuente
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    Clicks
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    HE
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    WIFI
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    HE %
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    Conv.
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.bySource || []).slice(0, 8).map((source, index) => (
                  <>
                  <tr
                    key={source.source}
                    className="border-b border-gray-50 dark:border-gray-800 last:border-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    onClick={() => setExpandedSource(expandedSource === source.source ? null : source.source)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {expandedSource === source.source 
                          ? <ChevronDown className="w-3 h-3 text-gray-400" />
                          : <ChevronRight className="w-3 h-3 text-gray-400" />
                        }
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {source.source || 'Sin fuente'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-600 dark:text-gray-300">
                      {source.clicks.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-green-600 dark:text-green-400">
                      {(source.heClicks ?? 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-400 dark:text-gray-500">
                      {(source.wifiClicks ?? 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={clsx(
                        'badge',
                        (source.heRate ?? 0) >= 60 ? 'badge-success' : (source.heRate ?? 0) >= 30 ? 'badge-warning' : 'badge-error'
                      )}>
                        {(source.heRate ?? 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-600 dark:text-gray-300">
                      {source.conversions.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={clsx(
                        'badge',
                        source.rate >= 5 ? 'badge-success' : source.rate >= 2 ? 'badge-warning' : 'badge-error'
                      )}>
                        {source.rate.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                  {expandedSource === source.source && (
                    (data?.bySourceProducts || [])
                      .filter(sp => sp.source === source.source)
                      .map(sp => (
                        <tr
                          key={`${sp.source}-${sp.productId}`}
                          className="bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-50 dark:border-gray-800"
                        >
                          <td className="px-4 py-2 pl-12">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {sp.productName}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
                            {sp.clicks.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-right text-xs tabular-nums text-green-600 dark:text-green-400">
                            {(sp.heClicks ?? 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-right text-xs tabular-nums text-gray-400 dark:text-gray-500">
                            {(sp.wifiClicks ?? 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
                            {(sp.heRate ?? 0).toFixed(1)}%
                          </td>
                          <td className="px-4 py-2 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
                            {sp.conversions.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="text-xs text-gray-400">
                              {sp.rate.toFixed(2)}%
                            </span>
                          </td>
                        </tr>
                      ))
                  )}
                  </>
                ))}
                {(!data?.bySource || data.bySource.length === 0) && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No hay datos disponibles
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* By Country Table */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Por País
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    País
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    Clicks
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    Conv.
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                    Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.byCountry || []).slice(0, 8).map((country) => (
                  <tr
                    key={country.country}
                    className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{country.flag}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {country.countryName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-600 dark:text-gray-300">
                      {country.clicks.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-600 dark:text-gray-300">
                      {country.conversions.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={clsx(
                        'badge',
                        country.rate >= 5 ? 'badge-success' : country.rate >= 2 ? 'badge-warning' : 'badge-error'
                      )}>
                        {country.rate.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                ))}
                {(!data?.byCountry || data.byCountry.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No hay datos disponibles
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
